import React from 'react';
import {
  Box, Button, ButtonText, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

export type PeerGoneBannerVariant = 'student' | 'lecturer';

export type PeerGoneBannerProps = {
  variant: PeerGoneBannerVariant;
  showClosingCountdown?: boolean;
  secondsRemaining?: number;
  errorMessage?: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
};

/**
 * 1:1 human peer-gone banner — distinct from AI examinerUnavailable copy.
 */
export function PeerGoneBanner({
  variant,
  showClosingCountdown = false,
  secondsRemaining = 0,
  errorMessage = null,
  onRetry,
  isRetrying = false,
}: PeerGoneBannerProps) {
  const { t } = useTranslation();

  const titleKey = variant === 'student'
    ? 'oralExam.peerGone.student.title'
    : 'oralExam.peerGone.lecturer.studentLeftTitle';
  const bodyKey = variant === 'student'
    ? 'oralExam.peerGone.student.body'
    : 'oralExam.peerGone.lecturer.studentLeftBody';
  const titleDefault = variant === 'student'
    ? 'Your supervisor left the room'
    : 'Student left';
  const bodyDefault = variant === 'student'
    ? 'If they don\'t return, we\'ll close the exam.'
    : 'We\'ll close the exam if they don\'t return.';

  return (
    <Box
      testID="oral-exam-peer-gone-banner"
      bg="$statusWarningSubtle"
      borderLeftWidth={4}
      borderLeftColor="$statusWarning"
      borderRadius="$sm"
      px="$4"
      py="$3"
      width="100%"
    >
      <VStack space="xs">
        <Text size="sm" fontWeight="semibold" color="$textHeading">
          {t(titleKey, { defaultValue: titleDefault })}
        </Text>
        <Text size="sm" color="$textBody">
          {t(bodyKey, { defaultValue: bodyDefault })}
        </Text>
        {showClosingCountdown ? (
          <HStack space="sm" alignItems="center" testID="oral-exam-peer-gone-closing-countdown">
            <Text size="sm" color="$textBody" fontWeight="medium">
              {t('oralExam.peerGone.closingExam', { defaultValue: 'Closing exam…' })}
            </Text>
            <Text size="sm" color="$textSubtle">
              {t('oralExam.peerGone.closingSeconds', {
                seconds: secondsRemaining,
                defaultValue: '{{seconds}}s',
              })}
            </Text>
          </HStack>
        ) : null}
        {errorMessage ? (
          <VStack space="xs" testID="oral-exam-peer-gone-error">
            <Text size="sm" color="$statusError">
              {errorMessage}
            </Text>
            {onRetry ? (
              <Button
                size="sm"
                variant="outline"
                action="secondary"
                onPress={onRetry}
                isDisabled={isRetrying}
                testID="oral-exam-peer-gone-retry"
              >
                <ButtonText>
                  {t('oralExam.peerGone.retryClose', { defaultValue: 'Try again' })}
                </ButtonText>
              </Button>
            ) : null}
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );
}
